/**
 * Teams slice - single source for teams API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { TEAMS } from '../../api/endpoints';

const errorMsg = (err) =>
  err.response?.data?.message ||
  (err.response?.status === 400 ? 'Invalid request.' : 'Request failed.');

export const getTeams = createAsyncThunk(
  'teams/getTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = {
        'relations[0]': 'members',
        'relations[1]': 'status',
        ...params,
      };
      if (queryParams.paginate === undefined) {
        queryParams.paginate = 0;
      }
      const { data } = await api.get(TEAMS.BASE, { params: queryParams });
      return data?.data ?? data;
    } catch (err) {
      toast.error(errorMsg(err));
      return rejectWithValue(errorMsg(err));
    }
  }
);

export const getTeamById = createAsyncThunk(
  'teams/getTeamById',
  async (teamId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(TEAMS.BY_ID(teamId), {
        params: {
          'relations[0]': 'members',
          'relations[1]': 'status',
          'relations[2]': 'members.roles',
        },
      });
      return data?.data ?? data;
    } catch (err) {
      toast.error(errorMsg(err));
      return rejectWithValue(errorMsg(err));
    }
  }
);

export const createTeam = createAsyncThunk(
  'teams/createTeam',
  async (teamData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(TEAMS.BASE, teamData);
      return data;
    } catch (err) {
      toast.error(errorMsg(err));
      return rejectWithValue(errorMsg(err));
    }
  }
);

export const updateTeam = createAsyncThunk(
  'teams/updateTeam',
  async ({ teamId, teamData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(TEAMS.BY_ID(teamId), teamData);
      return data;
    } catch (err) {
      toast.error(errorMsg(err));
      return rejectWithValue(errorMsg(err));
    }
  }
);

export const deleteTeam = createAsyncThunk(
  'teams/deleteTeam',
  async (teamId, { rejectWithValue }) => {
    try {
      await api.delete(TEAMS.BY_ID(teamId));
      return teamId;
    } catch (err) {
      toast.error(errorMsg(err));
      return rejectWithValue(errorMsg(err));
    }
  }
);

const initialState = {
  teams: null,
  currentTeam: null,
  loading: false,
  error: null,
};

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    clearCurrentTeam: (state) => {
      state.currentTeam = null;
    },
    clearTeamsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTeams.fulfilled, (state, action) => {
        state.loading = false;
        state.teams = action.payload;
      })
      .addCase(getTeamById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTeam = action.payload;
      })
      .addMatcher(
        (a) =>
          [getTeams.pending, getTeamById.pending, createTeam.pending, updateTeam.pending, deleteTeam.pending].some(
            (t) => t.match(a)
          ),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getTeams.rejected,
            getTeamById.rejected,
            createTeam.rejected,
            updateTeam.rejected,
            deleteTeam.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      )
      .addMatcher(
        (a) => [createTeam.fulfilled, updateTeam.fulfilled, deleteTeam.fulfilled].some((t) => t.match(a)),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentTeam, clearTeamsError } = teamsSlice.actions;
export default teamsSlice.reducer;
